import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ProvinceEntity } from 'src/app/core/services/location/domain/entities/province.entity';
import { LocalitiesEntity } from 'src/app/features/claims/data/models/location.model';
import { Departament } from 'src/app/features/user/presentation/components/New-User-SuperAdmin/newUser.component';
import { OfLegalAgeValidator } from 'src/app/core/utils/validator';
import { LocationEntity } from 'src/app/core/services/location/domain/entities/location.entity';
import { GetProvinceUseCase } from 'src/app/core/services/location/domain/usecases/get-province.usecase';
import { GetLocationsUseCase } from 'src/app/core/services/location/domain/usecases/get-locations.usecase';
import { GetDepartmentsUseCase } from 'src/app/core/services/location/domain/usecases/get-departments.usecase';
import { ClaimMultiEntity } from 'src/app/features/claims/domain/entities/claim-multi.entity';
import { GetFileModel } from 'src/app/features/claims/data/models/get-file.model';
import { Failure } from 'src/app/core/utils/failure';
import { GetFileMultiUseCase } from 'src/app/features/claims/domain/usecases/get-file-multi.usecase';
import { UserData } from 'src/app/features/user/domain/entities/user-data.entity';
import { GetCompanyCuilUseCase } from 'src/app/features/company/domain/usecases/get-company-colective.usecase';
import { debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-claimant-details-multi',
  templateUrl: './claimant-details-multi.component.html',
  styleUrls: ['./claimant-details-multi.component.css'],
})
export class ClaimantDetailsMultiComponent {
  @Output() claimantDetailsForm = new EventEmitter<FormGroup>();
  @Output() stepIndex: EventEmitter<number> = new EventEmitter();
  @Output() activeModal: EventEmitter<boolean> = new EventEmitter();
  @Input() claim?: ClaimMultiEntity;
  @Input() userData?: UserData;
  @Input() isEdit: boolean = false;
  @Input() activeIndex?: number;
  @Input() isRepresentative: boolean = false;
  readOnlyForEmployer: boolean = false;
  companyFound: boolean = true;

  formGroup!: FormGroup;

  provinces: ProvinceEntity[] = [];
  localities: LocalitiesEntity[] = [];
  departments: Departament[] = [];

  isDragging: boolean = false;
  maxDate: Date;
  minDate: Date = new Date(1930, 0, 1);
  pdfFiles: any[] = [];
  pdfFilesBase64: string[] = [];
  selectedCompanyId?: string | null;

  typeOfPerson = [
    { name: 'Persona física', key: 'physicalPerson' },
    { name: 'Persona jurídica', key: 'legalPerson' },
  ];

  constructor(
    private messageService: MessageService,
    private getProvince: GetProvinceUseCase,
    private getDepartmentsUsecase: GetDepartmentsUseCase,
    private getLocations: GetLocationsUseCase,
    private formBuilder: FormBuilder,
    private getFileUseCase: GetFileMultiUseCase,
    private getCompanyUseCase: GetCompanyCuilUseCase,
  ) {
    this.maxDate = new Date();
  }

  async ngOnInit(): Promise<void> {
    this.initForms();
    await this.loadProvinces();
    this.readOnlyForEmployer = !this.isRepresentative;
    await this.initializeComponent();

    if (this.isEdit) {
      this.initializeFormWithClaimData();
    } else {
      this.loadUserData();
    }
    this.setupCuitSearchListener();
  }

  private initForms(): void {
    this.formGroup = this.formBuilder.group({
      razonSocial: ['', Validators.required],
      companyCuit: ['', Validators.required],
      companyEmail: ['', [Validators.required, Validators.email]],
      companyPhone: [
        '',
        [
          Validators.minLength(7),
          Validators.maxLength(15),
        ],
      ],
      streetCompany: ['', Validators.required],
      numberCompany: ['', Validators.required],
      floorApartmentCompany: [''],
      towerCompany: [''],
      postalCodeCompany: [
        '',
        [
          Validators.required,
          Validators.maxLength(10),
          Validators.minLength(4),
        ],
      ],
      provinceCompany: ['', Validators.required],
      cityCompany: ['', Validators.required],
      claimantTypePerformance: ['', Validators.required],
      departmentId: ['', Validators.required],
      //datos personales
      lastName: [''],
      name: [''],
      cuil: [''],
      claimantPosition: [''],
      phone: [''],
      email: [''],
      birthday: [null],
      consent: [false, [Validators.requiredTrue]],
      file: [[], Validators.required],
      type_of_person: ['', Validators.required],
    });
    if (this.isRepresentative) {
      this.formGroup.get('departmentId')?.clearValidators();
      this.formGroup.get('departmentId')?.updateValueAndValidity();
      this.formGroup.get('file')?.clearValidators();
      this.formGroup.get('file')?.updateValueAndValidity();
      this.formGroup.get('claimantTypePerformance')?.clearValidators();
      this.formGroup.get('claimantTypePerformance')?.updateValueAndValidity();
    } else {
      this.formGroup.get('type_of_person')?.clearValidators();
      this.formGroup.get('type_of_person')?.updateValueAndValidity();
    }
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['claim'] && changes['claim'].currentValue) {
      await this.initializeFormWithClaimData();
    }
    if (changes['userData'] && changes['userData'].currentValue) {
      this.loadUserData();
    }
  }

  async initializeFormWithClaimData(): Promise<void> {
    this.formGroup.patchValue({
      razonSocial: this.claim?.razonSocial || '',
      companyCuit: this.claim?.companyCuit || '',
      companyEmail: this.claim?.companyEmail || '',
      companyPhone: this.claim?.companyPhone || '',
      streetCompany: this.claim?.streetCompany || '',
      numberCompany: this.claim?.numberCompany || '',
      floorApartmentCompany: this.claim?.floorApartmentCompany || '',
      towerCompany: this.claim?.towerCompany || '',
      postalCodeCompany: this.claim?.postalCodeCompany || '',
      provinceCompany:
        this.getProvinceIdByName(this.claim?.provinceCompany) || '',
      cityCompany: this.claim?.cityCompany || '',
      claimantTypePerformance: this.claim?.claimantTypePerformance || '',
      departmentId: this.claim?.departmentId || '',
      lastName: this.claim?.lastName || '',
      name: this.claim?.name || '',
      cuil: this.claim?.cuil || '',
      claimantPosition: this.claim?.claimantPosition || '',
      phone: this.claim?.phone || '',
      email: this.claim?.email || '',
      birthday: this.parseDateBirth(this.claim?.birthday || ''),
      consent: this.claim?.consent || false,
      file: this.claim?.file || '',
      type_of_person: this.claim?.type_of_person || '',
    });
    await this.checkFiles();
  }

  async loadUserData() {
    if (this.userData && this.readOnlyForEmployer) {
      this.selectedCompanyId = this.userData.company_cuit;
      const cuit = this.userData.company_cuit;
      const cuitLimpio = cuit?.replace(/-/g, '') ?? '';
      try {
        const result = await this.getCompanyUseCase.execute(cuitLimpio);
        if (result && 'razon_social' in result) {
          this.formGroup.patchValue({
            razonSocial: result.razon_social || '',
            companyCuit: cuitLimpio || '',
            companyEmail: result.email || '',
            companyPhone: result.phone || '',
            streetCompany: result.street || '',
            numberCompany: result.number.toString() || '',
            floorApartmentCompany: result.floor_apartment || '',
            towerCompany: result.tower || '',
            postalCodeCompany: result.postal_code.toString() || '',
            provinceCompany: result.province || '',
            cityCompany: result.city || '',
          });
        } else {
          console.error('No se encontró la empresa con el CUIT proporcionado.');
        }
      } catch (error) {
        console.error(
          'Ocurrió un error al buscar la información. Por favor, intente nuevamente o complete los datos manualmente.',
        );
      }

      this.formGroup.patchValue({
        lastName: this.userData.lastName,
        name: this.userData.name,
        cuil: this.userData.cuil,
        email: this.userData.email,
        phone: this.userData.phone,
        claimantTypePerformance: this.userData.tipo_actuacion,
        claimantPosition: this.userData.cargo_empresa,
        birthday: this.parseDateBirth(this.userData.birthDate || ''),
      });
    }
    this.updatePersonalFieldsValidation(this.userData?.tipo_actuacion);
  }

  private updatePersonalFieldsValidation(tipoActuacion?: string): void {
    const personalFields = [
      'lastName',
      'name',
      'cuil',
      'phone',
      'email',
      'birthday',
    ];

    if (tipoActuacion === 'Persona física') {
      personalFields.forEach((field) => {
        const control = this.formGroup.get(field);

        if (field === 'email') {
          control?.setValidators([Validators.required, Validators.email]);
        } else if (field === 'birthday') {
          control?.setValidators([Validators.required, OfLegalAgeValidator()]);
        } else if (field === 'phone') {
          control?.setValidators([
            Validators.minLength(7),
            Validators.maxLength(15),
          ]);
        } else {
          control?.setValidators([Validators.required]);
        }

        control?.updateValueAndValidity();
      });
    }
  }

  setupCuitSearchListener() {
    this.formGroup
      .get('companyCuit')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(async (value) => {
        if (this.isCuitInputComplete(value)) {
          await this.searchCompanyByCuit(value);
        }
      });
  }

  private isCuitInputComplete(value: unknown): boolean {
    const digits = String(value ?? '').replace(/\D/g, '');
    return digits.length === 11;
  }

  async searchCompanyByCuit(cuit: any) {
    if (!this.isCuitInputComplete(cuit)) {
      return;
    }
    try {
      const cuitLimpio = String(cuit ?? '').replace(/\D/g, '');
      const result = await this.getCompanyUseCase.execute(cuitLimpio);
      if (result && 'razon_social' in result) {
        this.companyFound = true;
        this.fillFormWithCompanyData(result);
      } else {
        this.companyFound = false;
        this.resetAfterCompanyNotFound(cuit);

        this.formGroup
          .get('provinceCompany')
          ?.valueChanges.subscribe((selectedProvince: any) => {
            if (selectedProvince) {
              this.loadLocalities(selectedProvince);
              this.formGroup.patchValue(
                { provincia: selectedProvince },
                { emitEvent: false },
              );
            }
          });
      }
      this.formGroup.updateValueAndValidity();
    } catch (error) {
      console.error('Error al buscar empresa por CUIT:', error);
      this.companyFound = false;
      this.resetAfterCompanyNotFound(cuit);
    }
  }

  private resetAfterCompanyNotFound(cuitMasked: any): void {
    const cuitActual = cuitMasked;
    this.formGroup.enable({ emitEvent: false });
    this.readOnlyForEmployer = !this.isRepresentative;
    this.localities = [];

    const claimantTypePerformance = !this.isRepresentative
      ? this.userData?.tipo_actuacion ?? ''
      : this.userData?.type_responsibility ?? '';

    this.formGroup.patchValue(
      {
        razonSocial: '',
        companyEmail: '',
        companyPhone: '',
        streetCompany: '',
        numberCompany: '',
        floorApartmentCompany: '',
        towerCompany: '',
        postalCodeCompany: '',
        provinceCompany: '',
        cityCompany: '',
        departmentId: '',
        claimantTypePerformance,
      },
      { emitEvent: false },
    );

    this.formGroup.get('companyCuit')?.setValue(cuitActual, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false });
  }

  async loadProvinces() {
    try {
      const result = await this.getProvince.execute();
      if (Array.isArray(result)) {
        this.provinces = result;
      }
    } catch (error) {
      console.error('Error fetching provinces', error);
    }
  }

  private getProvinceIdByName(
    provinceName: string | undefined,
  ): string | undefined {
    if (!provinceName) return undefined;
    let province = this.provinces.find((p) => p.id === provinceName);
    if (!province) {
      province = this.provinces.find((p) => p.name === provinceName);
    }
    return province ? province.name : undefined;
  }

  async loadLocalities(provinceId: string) {
    try {
      const localities = await this.getLocations.execute(provinceId);
      if (Array.isArray(localities)) {
        const localitiesList = this.putIdToLocalitiesList(localities);

        this.localities = localitiesList;
      }
    } catch (error) {
      console.error('Error fetching localities', error);
    }
  }

  putIdToLocalitiesList(list: LocationEntity[]): LocalitiesEntity[] {
    let listWithId: LocalitiesEntity[] = [];
    for (let i = 0; i < list.length; i++) {
      let city: LocalitiesEntity = {
        id: i,
        name: list[i].name,
      };
      listWithId.push(city);
    }
    return listWithId;
  }

  fillFormWithCompanyData(companyData: any) {
    this.formGroup.patchValue({
      razonSocial: companyData.razon_social || '',
      companyEmail: companyData.email || '',
      companyPhone: companyData.phone || '',
      streetCompany: companyData.street || '',
      numberCompany: companyData.number || '',
      floorApartmentCompany: companyData.floor_apartment || '',
      towerCompany: companyData.tower || '',
      postalCodeCompany: companyData.postal_code || '',
      provinceCompany: companyData.province || '',
      cityCompany: companyData.city || '',
      departmentId: companyData.departmentId || '',
      claimantTypePerformance: !this.isRepresentative
        ? this.userData?.tipo_actuacion || ''
        : companyData.type_responsibility || '',
    });
    const skip = [
      'file',
      'type_of_person',
      'consent',
      'departmentId',
      'companyCuit',
    ];
    Object.keys(this.formGroup.controls)
      .filter((key) => !skip.includes(key))
      .forEach((key) => {
        const control = this.formGroup.controls[key];
        const currentValue = control.value;
        control.disable({ emitEvent: false });
        control.setValue(currentValue, { emitEvent: false });
      });
    this.readOnlyForEmployer = true;
  }

  private async checkFiles(): Promise<void> {
    try {
      this.pdfFiles = await Promise.all(
        (this.claim?.file || []).map(async (fileName, index) => {
          const base64 = await this.getFile(
            new GetFileModel(this.claim!.id!, fileName),
          );
          return base64
            ? {
                name: `Archivo ${index + 1}`,
                base64: `data:application/pdf;base64,${base64}`,
              }
            : null;
        }),
      ).then((files) => files.filter((file) => file !== null));
      this.pdfFilesBase64 = this.pdfFiles.map((file) => file.base64);
      this.formGroup.get('file')?.setValue(this.pdfFilesBase64);
    } catch (error) {
      this.formGroup.get('file')?.setValue([]);
    }
  }
  async getFile(param: GetFileModel): Promise<string> {
    let result = await this.getFileUseCase.execute(param);
    if (result instanceof Failure) {
      return result.message;
    }
    return result;
  }

  async initializeComponent(): Promise<void> {
    this.loadDepartments();
  }

  async loadDepartments() {
    const departments = await this.getDepartmentsUsecase.execute();
    if (Array.isArray(departments)) {
      const transformedDepartments = this.putIdToDepartmentsList(departments);

      this.departments = [
        { id: 'Otro', name: 'Otro' },
        ...transformedDepartments,
      ];
    }
  }

  putIdToDepartmentsList(list: Departament[]): Departament[] {
    let listWithId: Departament[] = [];
    for (let i = 0; i < list.length; i++) {
      let department: Departament = {
        id: list[i].id,
        name: list[i].name,
      };
      listWithId.push(department);
    }
    return listWithId;
  }

  onDragOver(event: Event) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDropSuccess(event: any, file: string): any {
    const files = event.dataTransfer?.files;

    if (!files || files.length === 0) return;

    event.preventDefault();
    this.isDragging = false;
    this.onFileChange(files);
  }

  private onFileChange(files: any) {
    const newFiles = Array.from(files);
    this.pdfFiles = (this.pdfFiles || []).concat(newFiles);
  }

  onChange(event: any, file: string): any {
    const input = event.target as HTMLInputElement;
    const selectedFiles = Array.from(event.target.files) as File[];
    const fileForm = file;

    if (selectedFiles.length > 0) {
      if (this.pdfFiles.length >= 12) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            'Solo puedes subir hasta 12 archivos (JPG, WEBP, HEIF o PDF).',
        });
        return false;
      }

      const validTypes = [
        'image/jpeg',
        'image/png',
        'image/webp',
        'image/heif',
        'application/pdf',
      ];

      const validFiles = selectedFiles.filter((file) => {
        if (file.size > 20 * 1024 * 1024) {
          this.messageService.add({
            severity: 'error',
            summary: 'Archivo demasiado grande',
            detail: `El archivo "${file.name}" excede el límite de 20 MB.`,
          });
          return false;
        }

        if (!validTypes.includes(file.type)) {
          this.messageService.add({
            severity: 'error',
            summary: 'Tipo de archivo no permitido',
            detail: `El archivo "${file.name}" no es un formato válido.`,
          });
          return false;
        }

        return true;
      });

      validFiles.forEach((file) => {
        this.convertToBase64(file, fileForm);
      });

      input.value = '';
    }
  }

  private async convertToBase64(file: any, fileForm: string) {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result?.toString();
      if (base64String) {
        this.assignFilesReceipt(file, base64String);
      }
    };
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
    };
    reader.readAsDataURL(file);
  }

  private assignFilesReceipt(file: any, base64String: string) {
    if (this.pdfFiles.length >= 12) return;

    this.pdfFiles.push({ name: file.name, base64: base64String });

    let filePaycheckBase64Array = this.pdfFiles.map((file) => file.base64);
    this.formGroup.get('file')?.setValue(filePaycheckBase64Array);
    this.formGroup.get('file')?.markAsTouched();
    this.formGroup.get('file')?.updateValueAndValidity();
  }

  deleteFilePfd(index: number) {
    this.pdfFiles.splice(index, 1);
    const fileReceiptBase64Array = this.pdfFiles.map((file) => file.base64);
    this.formGroup
      .get('file')
      ?.setValue(fileReceiptBase64Array.length ? fileReceiptBase64Array : null);
    this.formGroup.get('file')?.markAsTouched();
    this.formGroup.get('file')?.markAsDirty();
  }

  hasErrors(controlName: string, errorType: string) {
    return (
      this.formGroup.get(controlName)?.hasError(errorType) &&
      this.formGroup.get(controlName)?.touched
    );
  }

  onConsentChange(event: any): void {
    this.formGroup.controls['consent'].setValue(event.checked);
  }

  parseDateBirth(date: any): Date | null {
    if (!date) return null;
    return new Date(date);
  }

  nextStep() {
    this.claimantDetailsForm.emit(this.formGroup);
    if (this.isRepresentative) {
      this.stepIndex.emit(2);
    } else {
      this.stepIndex.emit(1);
    }
  }

  cancel() {
    this.claimantDetailsForm.emit(this.formGroup);
    this.activeModal.emit(true);
  }

  back() {
    this.stepIndex.emit(0);
  }
}
